import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import { usePayrollRunQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import { entryWindowForPeriod } from '../../../registersMeta';
import {
    toRunPreviewRows,
    toRunTotalsRow,
    type RunPreviewRow,
    type RunTotalsRow,
} from '../../salariesMeta';
import { useSalaryRatesForPeriod } from '../../useSalaryRates';

type UseRunDetailDrawerParams = {
    /**
     * The run to show, by its **journal entry id** — a run has no other
     * identity. `null` closes the drawer.
     */
    journalEntryId: string | null;
    locale: LocaleCode;
};

/**
 * Logic behind the payroll run-detail drawer, so the component stays
 * render-only (the repo's triple pattern).
 *
 * Fetches the run and maps it to the same row/total shapes the run preview uses,
 * so a recorded run and a previewed one render identically. The figures are read
 * back from the run's **stored** lines, not recomputed — a raise recorded since
 * cannot reach backwards and rewrite what a posted period charged. The rate
 * labels follow the same rule: they come from the set in force on *that
 * period's* end date.
 */
export const useRunDetailDrawer = ({
    journalEntryId,
    locale,
}: UseRunDetailDrawerParams) => {
    const navigate = useNavigate();

    const query = usePayrollRunQuery(journalEntryId);
    const response = query.data;
    const run = response?.success ? response.response.run : null;
    const rates = useSalaryRatesForPeriod(run?.period ?? null);

    const rows = React.useMemo<RunPreviewRow[]>(
        () => (run ? toRunPreviewRows(run.lines, locale) : []),
        [run, locale],
    );
    const totals = React.useMemo<RunTotalsRow | null>(
        () => (run ? toRunTotalsRow(run.totals) : null),
        [run],
    );

    // The Journal **tab**, scoped to the run's own period. Payroll stamps its
    // entry with the period end (`periodEndDate(period)` server-side), so the
    // month always contains it — and the Books shell's window, which this tab
    // cannot widen, rarely does for anything but the current month.
    const onOpenEntry = React.useCallback(
        (entryId: string, period: string) => {
            const window = entryWindowForPeriod(period);
            if (!window) {
                return;
            }
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, window),
            );
        },
        [navigate],
    );

    return {
        run,
        rates,
        rows,
        totals,
        isLoading: query.isLoading && journalEntryId !== null,
        onOpenEntry,
    };
};

export type RunDetailDrawerModel = ReturnType<typeof useRunDetailDrawer>;
